import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2,
  Plus,
  Trash2,
  Edit3,
  Save,
  X,
  Package,
  Calendar,
  DollarSign,
  Target,
  Lightbulb,
  ListChecks,
  PauseCircle,
  PlayCircle,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const empty = {
  name: "",
  target: "",
  objective: "",
  deliverables: "",
  timeline: "",
  pricing_model: "",
  when_to_offer: "",
  commercial_notes: "",
  levels: [],
  order: 0,
  active: true,
};

const LEVELS = [
  { n: 1, name: "Crítico" },
  { n: 2, name: "Básico" },
  { n: 3, name: "Estruturado" },
  { n: 4, name: "Gerenciado" },
  { n: 5, name: "Avançado" },
];

const AdminProducts = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => {
    setLoading(true);
    api
      .get("/products")
      .then((r) => setItems(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openNew = () => {
    setEditing(null);
    setForm({ ...empty, order: items.length });
    setOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || "",
      target: p.target || "",
      objective: p.objective || "",
      deliverables: p.deliverables || "",
      timeline: p.timeline || "",
      pricing_model: p.pricing_model || "",
      when_to_offer: p.when_to_offer || "",
      commercial_notes: p.commercial_notes || "",
      levels: Array.isArray(p.levels) ? p.levels : [],
      order: p.order ?? 0,
      active: p.active !== false,
    });
    setOpen(true);
  };

  const toggleLevel = (n) => {
    setForm((f) => ({
      ...f,
      levels: f.levels.includes(n) ? f.levels.filter((x) => x !== n) : [...f.levels, n].sort(),
    }));
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Informe o nome do produto.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Produto atualizado.");
      } else {
        await api.post("/products", payload);
        toast.success("Produto criado.");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Excluir o produto "${p.name}"?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Produto excluído.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleActive = async (p) => {
    try {
      await api.put(`/products/${p.id}`, { ...p, active: !p.active });
      toast.success(p.active ? "Produto pausado." : "Produto ativado.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-3 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#6B8DB3]">
            Catálogo comercial
          </div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Package className="text-[#B9262B]" /> Produtos UniGR
          </h1>
          <p className="text-[#9aa3ad] mt-1">
            Soluções e módulos comerciais oferecidos ao cliente após o diagnóstico.
          </p>
        </div>
        <Button
          data-testid="new-product-btn"
          onClick={openNew}
          className="bg-[#B9262B] hover:bg-[#9a1e22] text-white"
        >
          <Plus size={16} className="mr-2" /> Novo produto
        </Button>
      </div>

      {loading ? (
        <div className="p-10 text-center text-[#6B8DB3]">
          <Loader2 className="animate-spin inline mr-2" /> Carregando...
        </div>
      ) : items.length === 0 ? (
        <div className="p-10 text-center text-[#6B8DB3] border border-dashed border-[#3A3F47] rounded-xl">
          Nenhum produto cadastrado.
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          {items.map((p) => (
            <article
              key={p.id}
              data-testid={`product-card-${p.id}`}
              className={`p-5 rounded-xl border bg-[#1C1F26] transition-all ${
                p.active
                  ? "border-[#3A3F47] hover:border-[#6B8DB3]/60"
                  : "border-[#3A3F47] opacity-60"
              }`}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex-1 min-w-0">
                  <div className="text-white font-semibold text-lg leading-tight">
                    {p.name}
                  </div>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    {p.active ? (
                      <span className="text-[10px] uppercase tracking-wider text-[#22B673] border border-[#22B673]/40 bg-[#22B673]/10 px-2 py-0.5 rounded-full">
                        Ativo
                      </span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wider text-[#F5A623] border border-[#F5A623]/40 bg-[#F5A623]/10 px-2 py-0.5 rounded-full">
                        Pausado
                      </span>
                    )}
                    {p.timeline && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-[#6B8DB3]">
                        <Calendar size={10} /> {p.timeline}
                      </span>
                    )}
                    {Array.isArray(p.levels) && p.levels.length > 0 && p.levels.length < 5 && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-[#B9262B] border border-[#B9262B]/40 bg-[#B9262B]/10 px-2 py-0.5 rounded-full">
                        Nível {p.levels.join(", ")}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1.5">
                  <button
                    onClick={() => toggleActive(p)}
                    title={p.active ? "Pausar" : "Ativar"}
                    className="p-1.5 rounded-md text-[#9aa3ad] hover:bg-[#0e1015] hover:text-white"
                  >
                    {p.active ? <PauseCircle size={16} /> : <PlayCircle size={16} />}
                  </button>
                  <button
                    onClick={() => openEdit(p)}
                    data-testid={`edit-product-${p.id}`}
                    className="p-1.5 rounded-md text-[#9aa3ad] hover:bg-[#0e1015] hover:text-white"
                  >
                    <Edit3 size={16} />
                  </button>
                  <button
                    onClick={() => remove(p)}
                    data-testid={`delete-product-${p.id}`}
                    className="p-1.5 rounded-md text-[#B9262B] hover:bg-[#B9262B]/15"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {p.target && (
                <Field icon={Target} label="Indicado para">
                  {p.target}
                </Field>
              )}
              {p.objective && (
                <Field icon={Lightbulb} label="Objetivo">
                  {p.objective}
                </Field>
              )}
              {p.deliverables && (
                <Field icon={ListChecks} label="Entregas principais">
                  {p.deliverables}
                </Field>
              )}
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                {p.pricing_model && (
                  <Field icon={DollarSign} label="Cobrança" compact>
                    {p.pricing_model}
                  </Field>
                )}
                {p.when_to_offer && (
                  <Field icon={Target} label="Quando ofertar" compact>
                    {p.when_to_offer}
                  </Field>
                )}
              </div>
              {p.commercial_notes && (
                <div className="mt-3 pt-3 border-t border-[#3A3F47]/60 text-[11px] text-[#9aa3ad] italic">
                  {p.commercial_notes}
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#1C1F26] border-[#3A3F47] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar produto" : "Novo produto UniGR"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <Row>
              <div className="sm:col-span-2">
                <FieldLabel>Nome do produto</FieldLabel>
                <Input
                  data-testid="product-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-[#0e1015] border-[#3A3F47] text-white mt-1"
                />
              </div>
              <div>
                <FieldLabel>Ordem</FieldLabel>
                <Input
                  type="number"
                  value={form.order}
                  onChange={(e) => setForm({ ...form, order: e.target.value })}
                  className="bg-[#0e1015] border-[#3A3F47] text-white mt-1"
                />
              </div>
            </Row>
            <div>
              <FieldLabel>Indicado para</FieldLabel>
              <Textarea
                rows={2}
                value={form.target}
                onChange={(e) => setForm({ ...form, target: e.target.value })}
                className="bg-[#0e1015] border-[#3A3F47] text-white mt-1"
              />
            </div>
            <div>
              <FieldLabel>Objetivo</FieldLabel>
              <Textarea
                rows={2}
                value={form.objective}
                onChange={(e) => setForm({ ...form, objective: e.target.value })}
                className="bg-[#0e1015] border-[#3A3F47] text-white mt-1"
              />
            </div>
            <div>
              <FieldLabel>Entregas principais</FieldLabel>
              <Textarea
                rows={3}
                value={form.deliverables}
                onChange={(e) => setForm({ ...form, deliverables: e.target.value })}
                className="bg-[#0e1015] border-[#3A3F47] text-white mt-1"
              />
            </div>
            <Row>
              <div>
                <FieldLabel>Prazo típico</FieldLabel>
                <Input
                  value={form.timeline}
                  onChange={(e) => setForm({ ...form, timeline: e.target.value })}
                  className="bg-[#0e1015] border-[#3A3F47] text-white mt-1"
                />
              </div>
              <div>
                <FieldLabel>Modelo de cobrança</FieldLabel>
                <Input
                  value={form.pricing_model}
                  onChange={(e) => setForm({ ...form, pricing_model: e.target.value })}
                  className="bg-[#0e1015] border-[#3A3F47] text-white mt-1"
                />
              </div>
            </Row>
            <div>
              <FieldLabel>Quando ofertar</FieldLabel>
              <Textarea
                rows={2}
                value={form.when_to_offer}
                onChange={(e) => setForm({ ...form, when_to_offer: e.target.value })}
                className="bg-[#0e1015] border-[#3A3F47] text-white mt-1"
              />
            </div>
            <div>
              <FieldLabel>Indicado para níveis de maturidade</FieldLabel>
              <div className="grid grid-cols-5 gap-2 mt-1.5">
                {LEVELS.map((lv) => {
                  const checked = form.levels.includes(lv.n);
                  return (
                    <button
                      key={lv.n}
                      type="button"
                      onClick={() => toggleLevel(lv.n)}
                      data-testid={`level-toggle-${lv.n}`}
                      className={[
                        "p-2 rounded-lg border text-center transition-all",
                        checked
                          ? "border-[#B9262B] bg-[#B9262B]/15 text-white"
                          : "border-[#3A3F47] bg-[#0e1015] text-[#9aa3ad] hover:border-[#6B8DB3]/60",
                      ].join(" ")}
                    >
                      <div className="font-mono font-bold text-base">{lv.n}</div>
                      <div className="text-[10px] uppercase tracking-wider">{lv.name}</div>
                    </button>
                  );
                })}
              </div>
              <div className="text-[10px] text-[#6B8DB3] mt-1.5">
                Deixe todos desmarcados para indicar que se aplica a todos os níveis.
              </div>
            </div>
            <div>
              <FieldLabel>Observações comerciais</FieldLabel>
              <Textarea
                rows={2}
                value={form.commercial_notes}
                onChange={(e) => setForm({ ...form, commercial_notes: e.target.value })}
                className="bg-[#0e1015] border-[#3A3F47] text-white mt-1"
              />
            </div>
            <div className="flex items-center gap-3 pt-2">
              <input
                id="active"
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
                className="w-4 h-4 accent-[#B9262B]"
              />
              <Label htmlFor="active" className="text-[#E6E6E8] text-sm cursor-pointer">
                Produto ativo (disponível para ofertar)
              </Label>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              className="border-[#3A3F47] text-white hover:bg-[#0e1015]"
            >
              <X size={14} className="mr-2" /> Cancelar
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              data-testid="save-product-btn"
              className="bg-[#B9262B] hover:bg-[#9a1e22] text-white"
            >
              {saving ? (
                <Loader2 className="animate-spin" />
              ) : (
                <>
                  <Save size={14} className="mr-2" /> Salvar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const FieldLabel = ({ children }) => (
  <Label className="text-[#9aa3ad] text-xs uppercase tracking-wider">{children}</Label>
);

const Row = ({ children }) => (
  <div className="grid sm:grid-cols-3 gap-3">{children}</div>
);

const Field = ({ icon: Icon, label, children, compact }) => (
  <div className={compact ? "" : "mt-3"}>
    <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#6B8DB3] mb-1">
      <Icon size={11} /> {label}
    </div>
    <div className={`text-[#E6E6E8] ${compact ? "text-xs" : "text-sm"} leading-relaxed`}>
      {children}
    </div>
  </div>
);

export default AdminProducts;
